"use client";

import { cn } from "@/lib/utils";
import { ComponentPropsWithoutRef, forwardRef, ReactNode } from "react";
import { Button } from "../button";
import Link from "next/link";

interface IProps {
  href?: string;
  icon?: ReactNode;
}

const GradientButtonIcon = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof Button> & IProps
>(({ children, className, icon, href, ...props }, ref) => {
  const buttonContent = (
    <Button
      ref={ref}
      className={cn(
        "group relative cursor-pointer overflow-hidden bg-radial-[at_bottom_right] from-[#100588] to-[#5231E7] to-60% rounded-full shadow-[0px_4px_11.6px_0px_rgba(82,49,231,0.30)] border-2 outline-white inline-flex justify-center items-center gap-1 md:gap-2 md:px-6.5 md:py-6.5 py-6 px-6 text-sm md:text-base font-medium md:font-semibold lg:text-base hover:scale-[1.02] transition-transform duration-300",
        className
      )}
      {...props}
    >
      {/* Shimmer Effect */}
      <span
        className="absolute inset-0 w-full h-full"
        style={{
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
          transform: "translateX(-100%)",
          animation: "shimmer 2.5s infinite"
        }}
      />

      <style jsx global>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          60% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>

      <p className="relative z-10">{children}</p>
      {icon && (
        <span className="relative z-10 items-center pb-0.5 md:pb-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
          {icon}
        </span>
      )}
    </Button>
  );

  return href ? <Link href={href}>{buttonContent}</Link> : buttonContent;
});

export default GradientButtonIcon;
